import React, { useCallback } from 'react';
import { useAlert } from 'src/contexts/alert-context';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import LabelSwitch from 'src/modules/shared/components/atoms/inputs/label-switch';
import { Text } from 'src/modules/shared/components/atoms/text';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { IntegrationPermission } from 'src/modules/shared/types/permissions.type';
import {
  useUpdateWhatsAppIntegrationPolicy,
  useWhatsAppIntegrationPolicy,
} from 'src/modules/shared/hooks/api/messaging/whatsapp/integrations';
import { WhatsAppBillingMode } from 'src/api/generated';

/** Restricts tenant billing policy visibility and changes to billing admins. */
export const WhatsAppBillingPolicyControl: React.FC = () => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { hasPermission: canManageBilling, loading: permissionLoading } =
    useHasPermissions(IntegrationPermission.ManageWhatsAppBilling);
  const {
    policy,
    loading: policyLoading,
    error: policyError,
    refetch: refetchPolicy,
  } = useWhatsAppIntegrationPolicy({ enabled: canManageBilling });
  const {
    updateWhatsAppIntegrationPolicy,
    loading: policyUpdating,
    error: policyUpdateError,
  } = useUpdateWhatsAppIntegrationPolicy();

  const handleBillingModeChange = useCallback(
    async (useComosManagedBilling: boolean) => {
      if (!policy || policyUpdating) return;

      try {
        await updateWhatsAppIntegrationPolicy({
          billingMode: useComosManagedBilling
            ? WhatsAppBillingMode.COMOS_MANAGED
            : WhatsAppBillingMode.CUSTOMER_MANAGED,
          expectedRevision: policy.revision,
        });
        showAlert(
          t('settings.integrations.whatsapp.billing.updateSuccess'),
          'success'
        );
      } catch {
        showAlert(
          t('settings.integrations.whatsapp.billing.updateError'),
          'error'
        );
        void refetchPolicy();
      }
    },
    [
      policy,
      policyUpdating,
      refetchPolicy,
      showAlert,
      t,
      updateWhatsAppIntegrationPolicy,
    ]
  );

  if (permissionLoading || !canManageBilling) return null;

  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-3 space-y-2">
      <Text size="small" weight="medium">
        {t('settings.integrations.whatsapp.billing.title')}
      </Text>
      {policyLoading && !policy ? (
        <div aria-label={t('settings.integrations.whatsapp.billing.loading')}>
          <TextSkeleton className="h-5 w-64" />
        </div>
      ) : policy ? (
        <LabelSwitch
          id="whatsapp-comos-managed-billing"
          label={t('settings.integrations.whatsapp.billing.label')}
          checked={policy.billingMode === WhatsAppBillingMode.COMOS_MANAGED}
          onChange={(checked) => void handleBillingModeChange(checked)}
          disabled={policyUpdating}
          helperText={t('settings.integrations.whatsapp.billing.helper')}
          labelPosition="top"
        />
      ) : null}
      {policyError || policyUpdateError ? (
        <Alert
          role="alert"
          size="sm"
          variant="danger"
          message={t(
            policyError
              ? 'settings.integrations.whatsapp.billing.loadError'
              : 'settings.integrations.whatsapp.billing.updateError'
          )}
        />
      ) : null}
    </div>
  );
};
